import React, { useEffect, useState } from 'react'
import styled, { css } from 'styled-components'
import { navigate, Link } from 'gatsby'
import { FaClock } from '@react-icons/all-files/fa/FaClock'
import { FaLocationArrow } from '@react-icons/all-files/fa/FaLocationArrow'
import { FaPhone } from '@react-icons/all-files/fa/FaPhone'
import { FaUser } from '@react-icons/all-files/fa/FaUser'
import { MdClose } from '@react-icons/all-files/md/MdClose'
import { MdMyLocation } from '@react-icons/all-files/md/MdMyLocation'
import { MdLocationOn } from '@react-icons/all-files/md/MdLocationOn'
import { IoIosSearch } from '@react-icons/all-files/io/IoIosSearch'
import formatHolidayHours from 'utils/location-hours/format-holiday-hours'
import sortLocationHours from 'utils/location-hours/sort-location-hours'
import UnstyledButton from 'components/button/unstyled-button'
import Notifications from './notifications'
import useMedia from 'react-use/lib/useMedia'
import { headerAnimationDuration } from '.'
import { useIsMounted } from 'hooks/use-is-mounted'
import { OutboundLink } from 'gatsby-plugin-google-gtag'
import { usePreferredLocation } from 'hooks/use-preferred-location'
import { LocationPickerModal } from 'components/location-picker-modal'
import { useToggle } from 'hooks/use-toggle'

const SecondaryNav = ({ setLocationMenuActive }) => {
  const { preferredLocation } = usePreferredLocation()
  const [isOpen, toggle] = useToggle()

  const isMounted = useIsMounted()

  const [storeHours, setStoreHours] = useState([])
  const [locationMenuAnimationActive, setLocationMenuAnimationActive] = useState(false)
  const [hideOriginalElements, setHideOriginalElements] = useState(false)
  const [hideLocationElements, setHideLocationElements] = useState(true)

  const isDesktop = useMedia('(min-width: 900px)')

  const originalElementsActiveClassName = hideOriginalElements
    ? 'display-none'
    : locationMenuAnimationActive
    ? ''
    : 'original-elements-active'

  useEffect(() => {
    if (!isDesktop) {
      setHideLocationElements(true)
      setHideOriginalElements(false)
      setLocationMenuActive(false)
    }
  }, [isDesktop, setLocationMenuActive])

  useEffect(() => {
    if (preferredLocation && preferredLocation.hours) {
      // Format hours and check for holidays
      let formattedHours = sortLocationHours(preferredLocation.hours)
      if (
        Array.isArray(preferredLocation.specialHours) &&
        preferredLocation.specialHours.length > 0
      ) {
        formattedHours = formatHolidayHours(formattedHours, preferredLocation.specialHours)
      }
      setStoreHours(formattedHours)
    }
  }, [preferredLocation])

  const openLocationMenu = () => {
    if (locationMenuAnimationActive) {
      return null
    }

    document.activeElement.blur()
    setLocationMenuActive(true)
    setLocationMenuAnimationActive(true)
    setHideLocationElements(false)

    setTimeout(() => {
      if (isMounted()) {
        setLocationMenuAnimationActive(false)
        setHideOriginalElements(true)
      }
    }, headerAnimationDuration)
  }

  const closeLocationMenu = () => {
    if (locationMenuAnimationActive) {
      return null
    }

    document.activeElement.blur()
    setLocationMenuActive(false)
    setLocationMenuAnimationActive(true)
    setHideOriginalElements(false)

    setTimeout(() => {
      if (isMounted()) {
        setLocationMenuAnimationActive(false)
        setHideLocationElements(true)
      }
    }, headerAnimationDuration)
  }

  const handleLocationButtonClick = () => {
    if (preferredLocation) {
      if (isDesktop) {
        openLocationMenu()
      } else {
        navigate(preferredLocation.slug)
      }
    } else {
      toggle()
    }
  }
  return (
    <React.Fragment>
      <SecondaryNavContainer>
        <SecondaryNavWrapper>
          <OriginalMenu className={hideOriginalElements ? 'display-none' : ''}>
            <LeftColumn>
              <Notifications className={originalElementsActiveClassName} />
            </LeftColumn>
            <RightColumn>
              <NavList>
                <li className='nav-link-desktop-only'>
                  <NavLink
                    as='a'
                    href='https://hutson.dealercustomerportal.com'
                    className={originalElementsActiveClassName}
                  >
                    <NavLinkIcon>
                      <FaUser aria-hidden='true' focusable='false' role='presentation' />
                    </NavLinkIcon>
                    <NavLinkText>Hutson Customer Portal</NavLinkText>
                  </NavLink>
                </li>
                <li>
                  <Location onClick={handleLocationButtonClick}>
                    <LocationInner className={originalElementsActiveClassName}>
                      <NavLinkIcon>
                        {!preferredLocation ? (
                          <MdMyLocation aria-hidden='true' focusable='false' role='presentation' />
                        ) : (
                          <MdLocationOn
                            aria-hidden='true'
                            focusable='false'
                            role='presentation'
                            style={{ fontSize: '16px' }}
                          />
                        )}
                      </NavLinkIcon>
                      <NavLinkText>
                        {!preferredLocation ? 'Find your store' : preferredLocation.title}
                      </NavLinkText>
                    </LocationInner>
                  </Location>
                </li>
                <li className='nav-link-mobile-only'>
                  <SearchNavLink to='/search/'>
                    <SearchNavLinkInner>
                      <SearchIcon>
                        <IoIosSearch aria-hidden='true' focusable='false' role='presentation' />
                      </SearchIcon>
                      <NavLinkText>Search</NavLinkText>
                    </SearchNavLinkInner>
                  </SearchNavLink>
                </li>
              </NavList>
            </RightColumn>
          </OriginalMenu>
          <LocationMenu>
            <LocationMenuContainer
              className={
                hideLocationElements
                  ? 'display-none'
                  : locationMenuAnimationActive
                  ? ''
                  : 'location-menu-active'
              }
            >
              <LocationMenuWrapper>
                {preferredLocation && (
                  <React.Fragment>
                    <LocationMenuLocation>
                      <LocationMenuLocationInner>
                        <LocationMenuLocationIcon
                          aria-hidden='true'
                          focusable='false'
                          role='presentation'
                        />
                        <LocationMenuLocationName>
                          {preferredLocation.title}
                        </LocationMenuLocationName>
                      </LocationMenuLocationInner>
                    </LocationMenuLocation>
                    <LocationInfoList>
                      {Array.isArray(storeHours) && storeHours.length > 0 && (
                        <li>
                          <LocationInfoIcon>
                            <FaClock aria-hidden='true' focusable='false' role='presentation' />
                          </LocationInfoIcon>
                          <LocationInfoText>
                            <LocationHoursStatus>
                              {storeHours[0].hours === 'Closed' ? 'Closed today' : 'Open today'}
                            </LocationHoursStatus>
                            <LocationHoursHours>
                              {storeHours[0].hours !== 'Closed'
                                ? storeHours[0].hours
                                : storeHours[0].title
                                ? storeHours[0].title
                                : ''}
                            </LocationHoursHours>
                          </LocationInfoText>
                        </li>
                      )}
                      <li>
                        <a href={`tel:${preferredLocation.phoneNumber}`}>
                          <LocationInfoIcon>
                            <FaPhone
                              aria-hidden='true'
                              focusable='false'
                              role='presentation'
                              style={{ transform: 'rotate(90deg)' }}
                            />
                          </LocationInfoIcon>
                          <LocationInfoText>{preferredLocation.phoneNumber}</LocationInfoText>
                        </a>
                      </li>
                      <li>
                        <OutboundLink href={preferredLocation.directionsLink}>
                          <LocationInfoIcon>
                            <FaLocationArrow
                              aria-hidden='true'
                              focusable='false'
                              role='presentation'
                            />
                          </LocationInfoIcon>
                          <LocationInfoText>{`${preferredLocation.address}, ${preferredLocation.city}, ${preferredLocation.state} ${preferredLocation.zip}`}</LocationInfoText>
                        </OutboundLink>
                      </li>
                    </LocationInfoList>
                    <LocationButtons>
                      <LocationButton onClick={toggle}>Change location</LocationButton>
                      <LocationButton as={Link} to={preferredLocation.slug}>
                        View location
                      </LocationButton>
                      <LocationMenuCloseButton onClick={closeLocationMenu}>
                        <MdClose aria-hidden='true' focusable='false' role='presentation' />
                      </LocationMenuCloseButton>
                    </LocationButtons>
                  </React.Fragment>
                )}
              </LocationMenuWrapper>
            </LocationMenuContainer>
          </LocationMenu>
        </SecondaryNavWrapper>
      </SecondaryNavContainer>
      <LocationPickerModal modalProps={{ isOpen, toggle }} />
    </React.Fragment>
  )
}

const SecondaryNavContainer = styled.div`
  background-color: #161414;
  height: auto;
  position: relative;
  transition: ${props => props.theme.transition};
  z-index: 1;

  @media (min-width: 900px) {
    background: radial-gradient(circle at 91% 0%, #377539 10%, transparent 5%), #161414;
    background-position: 91% 0%;
    background-repeat: no-repeat;
    background-size: ${props => (props.theme.locationMenuActive ? '1000% 1000%' : '0% 0%')};
    display: block;
    height: ${props => (props.theme.locationMenuActive ? '105px' : '65px')};
    padding: 0 40px;
  }
`

const SecondaryNavWrapper = styled.div`
  position: relative;

  @media (min-width: 900px) {
    height: ${props => (props.theme.locationMenuActive ? '80px' : '40px')};
  }
`

const OriginalMenu = styled.div`
  height: auto;
  position: relative;

  @media (min-width: 900px) {
    align-items: center;
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    height: 40px;
    justify-content: space-between;
  }
`

const LeftColumn = styled.div`
  display: block;
  height: auto;

  @media (min-width: 900px) {
    display: inline-block;
    flex-shrink: 1;
    flex-grow: 2;
    height: 40px;
  }
`

const RightColumn = styled.div`
  display: block;
  height: 50px;

  @media (min-width: 900px) {
    display: inline-block;
    flex-shrink: 0;
    height: 40px;
  }
`

const NavList = styled.ul`
  height: inherit;
  margin: 0;
  padding: 0;
  list-style: none;
  position: relative;

  > li {
    display: inline-block;
    height: inherit;
    margin: 0;
    position: relative;
    text-align: center;
    vertical-align: top;
    width: 50%;

    &.nav-link-desktop-only {
      display: none;

      @media (min-width: 900px) {
        display: inline-block;
      }
    }

    &.nav-link-mobile-only {
      display: inline-block;

      @media (min-width: 900px) {
        display: none;
      }
    }

    @media (min-width: 900px) {
      width: auto;
    }
  }
`

const NavBaseStyle = css`
  color: rgba(255, 255, 255, 0.7);
  display: block;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: -0.25px;
  padding: 0 10px;
  position: relative;
  text-decoration: none;

  :hover,
  :focus {
    text-decoration: underline;
  }
`

const NavLink = styled(Link)`
  ${NavBaseStyle}
  opacity: 0;
  top: 50%;
  transform: translateY(-50%);
  transition: ${props => props.theme.transition};

  &.original-elements-active {
    opacity: 1;
  }
`

const SearchNavLink = styled(NavLink)`
  background-color: #efefef;
  color: #161414;
  display: block;
  font-size: 16px;
  height: 100%;
  opacity: 1;
`

const SearchNavLinkInner = styled.div`
  position: relative;
  top: 50%;
  transform: translateY(-50%);
`

const SearchIcon = styled.div`
  display: inline-block;
  margin: -2px 5px 0 0;
  vertical-align: middle;

  svg {
    color: #161414;
    transform: rotate(90deg);
    vertical-align: middle;
  }
`

const NavLinkIcon = styled.div`
  display: inline-block;
  margin: -2px 5px 0 0;
  vertical-align: middle;

  svg {
    color: rgba(255, 255, 255, 0.7);
    vertical-align: middle;
  }
`

const NavLinkText = styled.span`
  display: inline;
  vertical-align: middle;
`

const Location = styled(UnstyledButton)`
  ${NavBaseStyle}
  background-color: #377539;
  color: rgba(255, 255, 255, 0.9);
  font-size: 16px;
  height: 100%;
  padding: 0 15px;
  text-align: center;
  width: 100%;

  svg {
    color: rgba(255, 255, 255, 0.9);
  }

  @media (min-width: 900px) {
    color: rgba(255, 255, 255, 0.7);
    font-size: 14px;
    margin-left: 10px;
    width: auto;

    svg {
      color: rgba(255, 255, 255, 0.7);
    }
  }
`

const LocationInner = styled.div`
  display: inline-block;
  opacity: 0;
  transition: ${props => props.theme.transition};
  vertical-align: middle;

  &.original-elements-active {
    opacity: 1;
  }
`

const LocationMenu = styled.div`
  display: none;

  @media (min-width: 900px) {
    display: block;
  }
`

const LocationMenuContainer = styled.div`
  height: 80px;
  opacity: 0;
  position: absolute;
  top: 0;
  transition: ${props => props.theme.transition};
  width: 100%;

  &.location-menu-active {
    opacity: 1;
  }
`

const LocationMenuWrapper = styled.div`
  align-items: center;
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  height: inherit;
  justify-content: space-between;
  position: relative;
`

const LocationMenuLocation = styled.div`
  display: inline-block;
  flex-shrink: 0;
  height: 100%;
  position: relative;
  vertical-align: top;

  :after {
    background-color: rgba(255, 255, 255, 0.7);
    content: '';
    display: inline-block;
    height: 40px;
    position: absolute;
    right: -20px;
    width: 1px;
  }
`

const LocationMenuLocationInner = styled.div`
  position: relative;
  top: 50%;
  transform: translateY(-50%);
`

const LocationMenuLocationIcon = styled(MdLocationOn)`
  color: #fff;
  font-size: 21px;
  margin-right: 7px;
  vertical-align: middle;
`

const LocationMenuLocationName = styled.span`
  color: #fff;
  font-size: 16px;
  vertical-align: middle;
`

const LocationInfoList = styled.ul`
  display: inline-block;
  flex-grow: 2;
  flex-shrink: 1;
  list-style: none;
  margin: 0 20px;
  padding: 0;
  position: relative;

  > li {
    color: rgba(255, 255, 255, 0.85);
    display: inline-block;
    font-size: 14px;
    margin: 0 0 0 20px;
    padding: 0;

    a {
      color: rgba(255, 255, 255, 0.85);
      text-decoration: none;

      :hover,
      :focus {
        text-decoration: underline;
      }
    }
  }
`

const LocationInfoIcon = styled.div`
  display: inline-block;
  margin: -2px 6px 0 0;
  vertical-align: middle;

  svg {
    color: rgba(255, 255, 255, 0.7);
    vertical-align: middle;
  }
`

const LocationInfoText = styled.span`
  display: inline;
  vertical-align: middle;
`

const LocationHoursStatus = styled.span`
  font-weight: 600;
`

const LocationHoursHours = styled.span`
  font-weight: 400;
  padding-left: 6px;
`

const LocationButtons = styled.div`
  display: inline-block;
  flex-shrink: 0;
  position: relative;
`

const LocationButton = styled(UnstyledButton)`
  background-color: #d7e3d7;
  color: #161414;
  display: inline-block;
  font-size: 16px;
  font-weight: 600;
  padding: 11px;
  margin: 0 10px 0 0;
  text-decoration: none;
  vertical-align: middle;

  :hover,
  :focus {
    text-decoration: underline;
  }
`

const LocationMenuCloseButton = styled(UnstyledButton)`
  display: inline-block;
  padding: 12px;
  vertical-align: middle;

  svg {
    color: #fff;
    font-size: 21px;
    vertical-align: middle;
  }
`

export default SecondaryNav
